import { useState } from 'react'
import { Plus, Trash2, Loader2, Mail, Copy } from 'lucide-react'
import {
  useEmailTemplates, useUpdateEmailTemplate,
  useCreateEmailVariation, useDeleteEmailVariation,
} from '@/hooks/useNaiDesk'
import type { NdEmailTemplate } from '@/hooks/useNaiDesk'

const CATEGORY_LABEL: Record<string, string> = {
  tickets: 'Chamados',
  improvements: 'Melhorias',
}

export default function EmailTemplatesAdmin() {
  const { data, isLoading } = useEmailTemplates()
  const [active, setActive] = useState<NdEmailTemplate | null>(null)
  const [filter, setFilter] = useState('')

  const filtered = (data || []).filter(t => !filter || t.category === filter)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="mb-6">
        <h1 className="text-2xl font-bold">Templates de Email</h1>
        <p className="text-sm text-gray-500">Editor de templates do Nai Desk com variações por módulo/subtipo</p>
      </header>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 text-xs rounded ${!filter ? 'bg-gray-900 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>Todos</button>
        <button onClick={() => setFilter('tickets')} className={`px-3 py-1.5 text-xs rounded ${filter === 'tickets' ? 'bg-gray-900 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>Chamados</button>
        <button onClick={() => setFilter('improvements')} className={`px-3 py-1.5 text-xs rounded ${filter === 'improvements' ? 'bg-gray-900 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>Melhorias</button>
      </div>

      {isLoading && <Loader2 className="h-5 w-5 animate-spin" />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(t => (
          <button key={t.id} onClick={() => setActive(t)} className="text-left bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 hover:border-blue-400 transition-colors">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{CATEGORY_LABEL[t.category] || t.category}</span>
                  <span className="text-xs text-gray-500">{t.recipient_tag}</span>
                  {t.supports_variations && <span className="text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-700">{t.variations.length} variações</span>}
                </div>
                <h3 className="font-semibold text-sm">{t.title}</h3>
                <p className="text-xs text-gray-500 mt-1 truncate">{t.subject}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {active && <Editor template={active} onClose={() => setActive(null)} />}
    </div>
  )
}

function Editor({ template, onClose }: { template: NdEmailTemplate; onClose: () => void }) {
  const update = useUpdateEmailTemplate()
  const createVar = useCreateEmailVariation()
  const deleteVar = useDeleteEmailVariation()
  const [subject, setSubject] = useState(template.subject)
  const [body, setBody] = useState(template.body)
  const [showVar, setShowVar] = useState(false)
  const [varModule, setVarModule] = useState('')
  const [varSubtype, setVarSubtype] = useState('')
  const [varSubject, setVarSubject] = useState(template.subject)
  const [varBody, setVarBody] = useState(template.body)

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <header className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="font-semibold text-lg">{template.title}</h3>
          <p className="text-xs text-gray-500 mt-1">base_key: <code>{template.base_key}</code></p>
        </header>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">Assunto</label>
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="w-full px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">Corpo HTML</label>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
              className="w-full px-3 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400 mb-1">Placeholders disponíveis</label>
            <div className="flex flex-wrap gap-1">
              {template.placeholders.map(p => (
                <button key={p} onClick={() => navigator.clipboard.writeText(`{{${p}}}`)}
                  className="text-xs px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-50 hover:text-blue-700 inline-flex items-center gap-1">
                  <Copy className="h-3 w-3" />{`{{${p}}}`}
                </button>
              ))}
            </div>
          </div>

          {template.supports_variations && (
            <div className="border-t border-gray-200 dark:border-gray-800 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-sm">Variações por módulo / subtipo</h4>
                <button onClick={() => setShowVar(!showVar)} className="text-xs px-3 py-1 rounded bg-blue-600 text-white inline-flex items-center gap-1"><Plus className="h-3 w-3" />Nova</button>
              </div>
              {showVar && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded p-3 mb-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input value={varModule} onChange={e => setVarModule(e.target.value)} placeholder="Módulo (opcional)" className="px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs" />
                    <input value={varSubtype} onChange={e => setVarSubtype(e.target.value)} placeholder="Subtipo (opcional)" className="px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs" />
                  </div>
                  <input value={varSubject} onChange={e => setVarSubject(e.target.value)} placeholder="Assunto" className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs" />
                  <textarea value={varBody} onChange={e => setVarBody(e.target.value)} rows={5} className="w-full px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-xs font-mono" />
                  <button onClick={async () => {
                    await createVar.mutateAsync({ template_id: template.id, module: varModule || null, subtype: varSubtype || null, subject: varSubject, body: varBody })
                    setShowVar(false)
                  }} className="px-3 py-1 rounded bg-emerald-600 text-white text-xs">Salvar variação</button>
                </div>
              )}
              <div className="space-y-2">
                {template.variations.map(v => (
                  <div key={v.id} className="bg-gray-50 dark:bg-gray-800 rounded px-3 py-2 flex items-center justify-between">
                    <div className="text-xs">
                      <span className="font-semibold">{v.module || '*'} / {v.subtype || '*'}</span>
                      <span className="text-gray-500 ml-2">{v.subject}</span>
                    </div>
                    <button onClick={() => confirm('Apagar variação?') && deleteVar.mutate(v.id)} className="text-red-600"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                {template.variations.length === 0 && <p className="text-xs text-gray-500">Nenhuma variação cadastrada</p>}
              </div>
            </div>
          )}
        </div>
        <footer className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm">Fechar</button>
          <button disabled={update.isPending} onClick={async () => { await update.mutateAsync({ id: template.id, payload: { subject, body } }); onClose() }}
            className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-semibold disabled:opacity-50">{update.isPending ? 'Salvando...' : 'Salvar'}</button>
        </footer>
      </div>
    </div>
  )
}
